import React from "react";
import { components } from "react-select";
import Avatar from "components/common/Avatar";

const API_URL = import.meta.env.VITE_API_URL || "";

export type UserOptionAttributes = {
  value: number;
  label: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  userEmail?: string;
  avatar?: string;
  role?: { id: number; code: string; name: string } | null;
  roleNames?: string;
};

function pickString(...vals: Array<unknown>): string {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

function getUserDisplayName(opt: UserOptionAttributes) {
  return (
    [opt.firstName, opt.lastName].filter(Boolean).join(" ").trim() || opt.label
  );
}

function getUserEmail(opt: UserOptionAttributes) {
  return pickString(opt.email, opt.userEmail);
}

function getAvatarImage(opt: UserOptionAttributes) {
  if (!opt.avatar) return undefined;
  if (opt.avatar.startsWith("http")) return opt.avatar;
  const path = opt.avatar.replace(/^\//, "");
  return path.startsWith("uploads/")
    ? `${API_URL}/${path}`
    : `${API_URL}/uploads/${path}`;
}

export function UserOption(props: any) {
  const { data, innerRef, innerProps, isDisabled } = props;
  const opt = data as UserOptionAttributes;
  const name = getUserDisplayName(opt);
  const email = getUserEmail(opt);
  const avatarUrl = getAvatarImage(opt);

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className="d-flex align-items-center gap-2 px-2 py-2"
      style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
    >
      <div className="flex-shrink-0" style={{ width: 32, height: 32 }}>
        <Avatar
          name={name}
          image={avatarUrl}
          className="rounded-circle w-100 h-100"
        />
      </div>
      <div className="flex-grow-1 min-w-0">
        <span className="text-body fw-medium d-block text-truncate">
          {name}
        </span>
        {email ? (
          <span className="text-muted small d-block text-truncate">{email}</span>
        ) : null}
        {(opt.role?.name ?? opt.roleNames) ? (
          <span className="badge bg-primary-subtle text-primary small mt-1">
            {opt.role?.name ?? opt.roleNames}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function UserSingleValue(props: any) {
  const { data } = props;
  const opt = data as UserOptionAttributes;
  const name = getUserDisplayName(opt);
  const email = getUserEmail(opt);
  const avatarUrl = getAvatarImage(opt);

  return (
    <components.SingleValue {...props}>
      <div className="d-flex align-items-center gap-2 w-100 overflow-hidden p-2">
        <div className="flex-shrink-0 avatar-sm">
          <Avatar
            name={name}
            image={avatarUrl}
            className="rounded-circle w-100 h-100"
          />
        </div>
        <div className="min-w-0 text-truncate d-flex flex-column gap-0">
          <span className="fw-medium text-truncate">{name}</span>
          {email ? (
            <span className="text-muted small text-truncate">{email}</span>
          ) : null}
          {opt.roleNames && (
            <div className="d-flex flex-wrap gap-1 mt-0">
              {opt.roleNames
                .split(",")
                .map((role) => role.trim())
                .filter(Boolean)
                .map((role) => (
                  <span
                    key={role}
                    className="badge bg-primary-subtle text-primary small"
                  >
                    {role}
                  </span>
                ))}
            </div>
          )}
        </div>
      </div>
    </components.SingleValue>
  );
}
